import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Crown, Gift, PackagePlus, SlidersHorizontal, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';

// Gestão COMERCIAL de uma conta pelo dono da plataforma: plano (Pro cortesia com prazo /
// Free), cortesia total, add-ons de cortesia, limites do Free e dados da conta. Nada aqui
// gera cobrança: a cortesia é da plataforma.

type Limits = {
  max_projects: number; max_numbers_per_project: number; max_users: number; max_api_keys: number;
  storage_mb: number; media_retention_days: number; message_retention_days: number; rate_limit_rps: number;
};
type Overrides = { [K in keyof Limits]: number | null };
interface Effective extends Limits { plan: string; has_campaigns?: boolean; has_schedule_year?: boolean; schedule_max_hours?: number }
interface State {
  plan: string; plan_comp: boolean; renews_at?: string; cancel_at?: string; grace_until?: string; recurring: boolean;
  exempt: boolean; exempt_until?: string;
  paid_addons: Record<string, number>; grants: Record<string, number>;
  free_limits: Overrides; default_limits: Limits; effective: Effective;
}
interface Commercial {
  state: State;
  account: { name: string; locale: string; external_id: string; currency: string };
  currencies: string[]; locales: string[];
}

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
// Data LOCAL (yyyy-mm-dd) — toISOString() usa UTC e o fim do dia em Brasília viraria o dia seguinte.
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const plusDays = (n: number, from?: string) => {
  const base = from && new Date(from) > new Date() ? new Date(from) : new Date();
  base.setDate(base.getDate() + n);
  return isoDay(base);
};
const lim = (v: number) => (v === -1 ? 'ilimitado' : v.toLocaleString('pt-BR'));

const ADDONS: { kind: string; label: string; hint: string; feature?: boolean }[] = [
  { kind: 'number', label: 'Números extras', hint: '+1 número por projeto cada' },
  { kind: 'project', label: 'Projetos extras', hint: '+1 projeto cada' },
  { kind: 'storage_gb', label: 'Armazenamento (GB)', hint: '+1 GB cada' },
  { kind: 'retention_block', label: 'Retenção (+30 dias)', hint: '+30 dias de histórico cada' },
  { kind: 'campaigns', label: 'Campanhas', hint: 'libera disparo em massa', feature: true },
  { kind: 'schedule_year', label: 'Agendamento até 1 ano', hint: 'em vez de 30 dias', feature: true },
];

const LIMIT_FIELDS: { key: keyof Limits; label: string }[] = [
  { key: 'max_projects', label: 'Projetos' },
  { key: 'max_numbers_per_project', label: 'Números por projeto' },
  { key: 'max_users', label: 'Usuários' },
  { key: 'max_api_keys', label: 'API keys' },
  { key: 'storage_mb', label: 'Armazenamento (MB)' },
  { key: 'message_retention_days', label: 'Retenção de mensagens (dias)' },
  { key: 'media_retention_days', label: 'Retenção de mídia (dias)' },
  { key: 'rate_limit_rps', label: 'Requisições por segundo' },
];

type Confirm = { title: string; body: string; action: () => Promise<void> } | null;

export function TenantCommercial({ id, disabled, onChanged }: { id: string; disabled?: boolean; onChanged?: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['platform-tenant-commercial', id],
    queryFn: async (): Promise<Commercial | null> => {
      const r = await apiFetch('GET', `/platform/tenants/${id}/commercial`);
      return r.ok ? (r.data as Commercial) : null;
    },
  });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  // Formulários locais, ressincronizados quando o servidor responde.
  const [proUntil, setProUntil] = useState('');
  const [courtesyUntil, setCourtesyUntil] = useState('');
  const [grants, setGrants] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [acc, setAcc] = useState({ name: '', locale: '', external_id: '', currency: '' });

  const d = q.data;
  useEffect(() => {
    if (!d) return;
    const s = d.state;
    setProUntil(s.plan === 'pro' && s.plan_comp && s.renews_at ? isoDay(new Date(s.renews_at)) : plusDays(30));
    setCourtesyUntil(s.exempt_until ? isoDay(new Date(s.exempt_until)) : '');
    setGrants(Object.fromEntries(ADDONS.map((a) => [a.kind, String(s.grants?.[a.kind] ?? 0)])));
    setLimits(Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, s.free_limits?.[f.key] == null ? '' : String(s.free_limits[f.key])])));
    setAcc({ ...d.account });
  }, [d]);

  const call = async (method: string, path: string, body: unknown, ok: string) => {
    setBusy(true);
    try {
      const r = await apiFetch(method, `/platform/tenants/${id}${path}`, body);
      if (!r.ok) {
        const e = r.data as { message?: string } | undefined;
        toast.error(e?.message || 'Não foi possível salvar.');
        return;
      }
      qc.setQueryData(['platform-tenant-commercial', id], r.data);
      qc.invalidateQueries({ queryKey: ['platform-tenant', id] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      onChanged?.();
      toast.success(ok);
    } finally { setBusy(false); }
  };

  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!d) return null;
  const s = d.state;
  const paidPro = s.plan === 'pro' && !s.plan_comp;
  const compPro = s.plan === 'pro' && s.plan_comp;
  const off = disabled || busy;

  const status = s.exempt
    ? { label: 'Cortesia total', detail: s.exempt_until ? `até ${fmtDate(s.exempt_until)}` : 'sem prazo', variant: 'warning' as const }
    : compPro
      ? { label: 'Pro cortesia', detail: `até ${fmtDate(s.renews_at)}`, variant: 'success' as const }
      : paidPro
        ? {
          label: 'Pro pago',
          detail: s.cancel_at ? `cancelado — cai para o Free em ${fmtDate(s.cancel_at)}`
            : s.grace_until ? `em atraso — carência até ${fmtDate(s.grace_until)}`
              : `renova em ${fmtDate(s.renews_at)}${s.recurring ? ' (cartão automático)' : ''}`,
          variant: 'success' as const,
        }
        : { label: 'Free', detail: 'sem assinatura', variant: 'secondary' as const };

  const saveGrants = () => call('PUT', '/grants',
    Object.fromEntries(ADDONS.map((a) => [a.kind, Math.max(0, parseInt(grants[a.kind] || '0', 10) || 0)])),
    'Add-ons de cortesia salvos.');
  const saveLimits = () => {
    const body: Record<string, number | null> = {};
    for (const f of LIMIT_FIELDS) {
      const v = (limits[f.key] ?? '').trim();
      body[f.key] = v === '' ? null : parseInt(v, 10);
    }
    return call('PUT', '/free-limits', body, 'Limites do Free salvos.');
  };
  const saveAccount = () => call('PATCH', '/account', acc, 'Dados da conta salvos.');

  const eff = s.effective;
  return (
    <>
      {/* Plano e cortesia */}
      <Card className="min-w-0">
        <CardHeader className="flex-row items-center gap-2">
          <Crown className="size-4 text-primary" />
          <CardTitle>Plano e cortesia</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={status.variant}>{status.label}</Badge>
            <span className="text-muted-foreground">{status.detail}</span>
          </div>
          <div className="grid gap-2 rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground sm:grid-cols-4">
            <span>Projetos: <b className="text-foreground">{lim(eff.max_projects)}</b></span>
            <span>Números/projeto: <b className="text-foreground">{lim(eff.max_numbers_per_project)}</b></span>
            <span>Usuários: <b className="text-foreground">{lim(eff.max_users)}</b></span>
            <span>API keys: <b className="text-foreground">{lim(eff.max_api_keys)}</b></span>
            <span>Armazenamento: <b className="text-foreground">{eff.storage_mb === -1 ? 'ilimitado' : `${(eff.storage_mb / 1024).toLocaleString('pt-BR')} GB`}</b></span>
            <span>Retenção: <b className="text-foreground">{eff.message_retention_days === -1 ? 'ilimitada' : `${eff.message_retention_days} dias`}</b></span>
            <span>Campanhas: <b className="text-foreground">{eff.has_campaigns ? 'sim' : 'não'}</b></span>
            <span>Agendar até: <b className="text-foreground">{eff.schedule_max_hours ? (eff.schedule_max_hours >= 8760 ? '1 ano' : eff.schedule_max_hours >= 720 ? '30 dias' : '24 h') : '—'}</b></span>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Pro cortesia */}
            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <p className="text-sm font-semibold">Pro cortesia</p>
              <p className="text-xs text-muted-foreground">
                O cliente usa o Pro sem pagar até a data. Nenhuma fatura é gerada; no vencimento a conta volta ao Free sozinha.
              </p>
              {paidPro ? (
                <p className="text-xs text-amber-700 dark:text-amber-400">Esta conta paga o Pro. Para dar cortesia, use a cortesia total ou volte-a ao Free antes.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="pro-until" className="text-xs">Até</Label>
                      <Input id="pro-until" type="date" className="w-44" value={proUntil} min={isoDay(new Date())} onChange={(e) => setProUntil(e.target.value)} />
                    </div>
                    {[30, 90, 365].map((n) => (
                      <Button key={n} size="sm" variant="outline" disabled={off} onClick={() => setProUntil(plusDays(n, compPro ? s.renews_at : undefined))}>
                        +{n === 365 ? '1 ano' : `${n} dias`}
                      </Button>
                    ))}
                  </div>
                  <Button className="self-start" disabled={off || !proUntil} onClick={() => call('PUT', '/plan', { plan: 'pro', until: proUntil }, compPro ? 'Pro cortesia estendido.' : 'Pro cortesia liberado.')}>
                    {compPro ? 'Salvar novo prazo' : 'Dar Pro cortesia'}
                  </Button>
                </>
              )}
            </div>

            {/* Cortesia total */}
            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <p className="flex items-center gap-1.5 text-sm font-semibold"><Gift className="size-4" /> Cortesia total</p>
              <p className="text-xs text-muted-foreground">
                Tudo ilimitado e sem cobrança, acima de qualquer plano. Deixe a data vazia para não ter prazo.
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="court-until" className="text-xs">Até (opcional)</Label>
                <Input id="court-until" type="date" className="w-44" value={courtesyUntil} min={isoDay(new Date())} onChange={(e) => setCourtesyUntil(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button disabled={off} onClick={() => call('PUT', '/courtesy', { enabled: true, until: courtesyUntil }, s.exempt ? 'Prazo da cortesia salvo.' : 'Cortesia total liberada.')}>
                  {s.exempt ? 'Salvar prazo' : 'Liberar cortesia total'}
                </Button>
                {s.exempt && (
                  <Button variant="outline" disabled={off} onClick={() => setConfirm({
                    title: 'Encerrar a cortesia total?',
                    body: 'A conta passa a seguir o plano atual. Números e projetos acima do limite desse plano são desconectados/arquivados agora (os dados ficam guardados).',
                    action: () => call('PUT', '/courtesy', { enabled: false }, 'Cortesia encerrada.'),
                  })}>Encerrar cortesia</Button>
                )}
              </div>
            </div>
          </div>

          {s.plan === 'pro' && (
            <Button variant="outline" className="self-start" disabled={off} onClick={() => setConfirm({
              title: 'Voltar esta conta ao Free agora?',
              body: paidPro
                ? 'O Pro PAGO termina agora, sem reembolso automático do ciclo. Add-ons pagos e carrinho são zerados, faturas em aberto são anuladas e números/projetos acima do Free são desconectados/arquivados (dados guardados).'
                : 'O Pro cortesia termina agora. Números/projetos acima do Free são desconectados/arquivados (dados guardados).',
              action: () => call('PUT', '/plan', { plan: 'free' }, 'Conta voltou ao Free.'),
            })}>Voltar ao Free</Button>
          )}
        </CardContent>
      </Card>

      {/* Add-ons de cortesia */}
      <Card className="min-w-0">
        <CardHeader className="flex-row items-center gap-2">
          <PackagePlus className="size-4 text-primary" />
          <CardTitle>Add-ons de cortesia</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Somam aos add-ons que o cliente paga e <strong>não entram na mensalidade</strong>. Valem enquanto a conta estiver no Pro (pago ou cortesia).
            {s.plan !== 'pro' && !s.exempt && <> <span className="text-amber-700 dark:text-amber-400">A conta está no Free: só passam a valer quando ela estiver no Pro.</span></>}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ADDONS.map((a) => (
              <div key={a.kind} className="flex flex-col gap-1.5 rounded-lg border p-3">
                <Label className="text-xs">{a.label}</Label>
                {a.feature ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="size-4 accent-primary" checked={grants[a.kind] === '1'} disabled={off}
                      onChange={(e) => setGrants((g) => ({ ...g, [a.kind]: e.target.checked ? '1' : '0' }))} />
                    Liberar
                  </label>
                ) : (
                  <Input type="number" min={0} max={1000} value={grants[a.kind] ?? '0'} disabled={off}
                    onChange={(e) => setGrants((g) => ({ ...g, [a.kind]: e.target.value }))} />
                )}
                <p className="text-xs text-muted-foreground">
                  {a.hint}{(s.paid_addons?.[a.kind] ?? 0) > 0 && <> · pago: <b>{s.paid_addons[a.kind]}</b></>}
                </p>
              </div>
            ))}
          </div>
          <Button className="self-start" disabled={off} onClick={saveGrants}>Salvar add-ons</Button>
        </CardContent>
      </Card>

      {/* Limites do Free */}
      <Card className="min-w-0">
        <CardHeader className="flex-row items-center gap-2">
          <SlidersHorizontal className="size-4 text-primary" />
          <CardTitle>Limites do plano Free desta conta</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Valem enquanto a conta estiver no Free. Vazio = padrão da plataforma (mostrado no campo); <strong>-1</strong> = ilimitado.
            Baixar um limite não desconecta nada: vale para o que for criado daqui em diante.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {LIMIT_FIELDS.map((f) => (
              <div key={f.key} className="flex flex-col gap-1.5">
                <Label className="text-xs">{f.label}</Label>
                <Input type="number" min={-1} placeholder={`padrão: ${lim(s.default_limits[f.key])}`} value={limits[f.key] ?? ''} disabled={off}
                  onChange={(e) => setLimits((l) => ({ ...l, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <Button className="self-start" disabled={off} onClick={saveLimits}>Salvar limites</Button>
        </CardContent>
      </Card>

      {/* Dados da conta */}
      <Card className="min-w-0">
        <CardHeader className="flex-row items-center gap-2">
          <Building2 className="size-4 text-primary" />
          <CardTitle>Dados da conta</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Nome</Label>
              <Input value={acc.name} disabled={off} onChange={(e) => setAcc((a) => ({ ...a, name: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Idioma</Label>
              <Select value={acc.locale || undefined} onValueChange={(v) => setAcc((a) => ({ ...a, locale: v }))} disabled={off}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{d.locales.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Moeda</Label>
              <Select value={acc.currency || undefined} onValueChange={(v) => setAcc((a) => ({ ...a, currency: v }))} disabled={off || paidPro}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{d.currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              {paidPro && <p className="text-xs text-muted-foreground">Não muda com Pro pago.</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">ID externo</Label>
              <Input value={acc.external_id} placeholder="ex.: id no seu ERP" disabled={off} onChange={(e) => setAcc((a) => ({ ...a, external_id: e.target.value }))} />
            </div>
          </div>
          <Button className="self-start" disabled={off || !acc.name.trim()} onClick={saveAccount}>Salvar dados</Button>
        </CardContent>
      </Card>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogDescription>{confirm?.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={busy} onClick={async () => { const c = confirm; setConfirm(null); await c?.action(); }}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
